import * as Immutable from 'immutable';
import { handleActions } from 'redux-actions';

import { checkIfValueExists, getLanguageCode } from '../../shared/helpers';

import {
  checkIfSettingsAreInvalid,
  getSettings,
  getSettingsGivenOtherSettings,
  multiSimulationSettingsChildToFilter,
  multiSimulationSettingsToFilter,
  simulationSettingsToFilter,
} from './EditorHelper';
import { EditorActionType, EditorStoreElement, EditorTab } from './EditorReducer.types';
import { WALKTHROUGH_EXAMPLE_CODE } from './constants';
import { SIMULATE_DATA_EXPLORER, SIMULATE_LEARN } from '../../shared/constants';

const languageCode: string = getLanguageCode();

// @ts-ignore
const initialState: EditorStoreElement = Immutable.fromJS({
  tabs: Immutable.List(),
  activeTab: -1,
  tabCount: 0,
  multiTabCount: 0,
  superTabCount: 0,
  invalidIds: [],
  exampleArrayIds: [],
  isMonacoInitialized: false,
  options: null,
  fetchingSettings: false,
  loadedOptions: false,
  codeEditorWidth: 890,
  isChangingTabPosition: false,
  dataExplorerPathname: SIMULATE_DATA_EXPLORER,
  learnPathname: SIMULATE_LEARN,
  isSimulated: false,
});

const createNewChildTab = (
  state: EditorStoreElement,
  action: EditorActionType,
  settings?: any,
  code: string = '',
  addNewInvalidIds: Boolean = false
) => {
  const tabs: Immutable.List<any> = state.get('tabs');
  const activeMultiTab = state.get('activeTab') || 0;
  // @ts-ignore
  const multiTabCount: number = state.getIn(['tabs', activeMultiTab, 'tabNumber']);
  // @ts-ignore
  const childTabCountPrev: number = state.getIn(['tabs', activeMultiTab, 'childTabCount']) || 0;
  const childTabCount = childTabCountPrev + 1;
  const defCount: number = tabs.get(activeMultiTab).get('childTabs').size;
  const tabCharPrev: string = String.fromCharCode(childTabCount + 97);
  const tabChar = tabCharPrev.toUpperCase();
  const currentMultiChildName = `Simulation ${multiTabCount ? multiTabCount.toLocaleString(languageCode) : ''}${tabChar}`;
  const allAppTabs = tabs;
  const id = action.payload.idChilds[0] ? action.payload.idChilds[0] : action.payload.idParent;
  const newInvalidIds = state.get('invalidIds');

  // @ts-ignore
  const newMultiChildElement: EditorTab = Immutable.fromJS({
    tabName: currentMultiChildName,
    id,
    code,
    codeError: null,
    settings: settings
      ? settings
      : getSettings(state.get('options'), Immutable.fromJS(action.payload.data), multiSimulationSettingsChildToFilter),
    tabNumber: tabChar,
  });
  const thisChildTabs = allAppTabs.get(activeMultiTab).get('childTabs').push(newMultiChildElement);
  const updatedMultiTab: EditorTab = tabs.get(activeMultiTab).set('activeChildTab', defCount).set('childTabs', thisChildTabs);

  return state
    .set('tabs', allAppTabs.set(activeMultiTab, updatedMultiTab))
    .set('invalidIds', addNewInvalidIds ? newInvalidIds.push(id) : newInvalidIds)
    .setIn(['tabs', activeMultiTab, 'childTabCount'], childTabCount);
};

const updateSettingsTabActive = (state: EditorStoreElement, action: EditorActionType, settings: any = action.payload.settings) => {
  const tabs = state.get('tabs');
  const activeIdx = (action.payload && action.payload.tabIndex) || 0;
  let allTabs = tabs;
  if (action.payload && action.payload.childActiveIndex !== -1) {
    const parentSettings = getSettingsGivenOtherSettings(Immutable.fromJS(settings), multiSimulationSettingsToFilter);
    const childSettings = getSettingsGivenOtherSettings(Immutable.fromJS(settings), multiSimulationSettingsChildToFilter);

    allTabs = allTabs
      .setIn([activeIdx, 'settings'], parentSettings)
      .setIn([activeIdx, 'childTabs', action.payload.childActiveIndex || 0, 'settings'], childSettings);

    const childTabs = allTabs
      .getIn([activeIdx, 'childTabs'])
      .map((tab: any) => tab.set('settings', checkIfSettingsAreInvalid(tab.get('settings'), parentSettings, state.get('options'))));
    allTabs = allTabs.setIn([activeIdx, 'childTabs'], childTabs);
  } else {
    allTabs = allTabs.setIn([activeIdx, 'settings'], Immutable.fromJS(settings));
  }

  return state.set('tabs', allTabs);
};

const updateCodeTabActive = (state: EditorStoreElement, action: EditorActionType, newcode: any = action.payload.code) => {
  const tabs = state.get('tabs');
  const activeIndex = (action.payload && action.payload.tabIndex) || 0;
  const activeChildIndex = (action.payload && action.payload.childActiveIndex) || 0;
  const code = newcode ? newcode : '';
  let updatedTabs = tabs;
  let newInvalidIds = state.get('invalidIds');
  let exampleArrayIds = state.get('exampleArrayIds');

  if (action.payload && action.payload.childActiveIndex !== -1) {
    const parentTabId = updatedTabs.getIn([activeIndex, 'id']);
    const numOfInvalidChilds = updatedTabs.getIn([activeIndex, 'childTabs']).reduce((total: number, childTab: any) => {
      if (newInvalidIds.includes(childTab.get('id'))) {
        return total + 1;
      }

      return total;
    }, 0);
    const id = updatedTabs.getIn([activeIndex, 'childTabs', activeChildIndex, 'id']);
    exampleArrayIds = exampleArrayIds.filter((element: string) => element !== id);

    if (code.trim().length > 0 && newInvalidIds.indexOf(id) < 0) {
      newInvalidIds = newInvalidIds.push(id);
      if (numOfInvalidChilds === 0) {
        newInvalidIds = newInvalidIds.push(parentTabId);
      }
    } else if (code.trim().length === 0 && newInvalidIds.indexOf(id) > -1) {
      newInvalidIds = newInvalidIds.filter((element: string) => element !== id);
      if (numOfInvalidChilds === 1) {
        newInvalidIds = newInvalidIds.filter((element: string) => element !== parentTabId);
      }
    }
    updatedTabs = updatedTabs
      .setIn([activeIndex, 'childTabs', activeChildIndex, 'code'], code)
      .setIn([activeIndex, 'childTabs', activeChildIndex, 'codeError'], null);
  } else {
    const id = updatedTabs.getIn([activeIndex, 'id']);
    exampleArrayIds = exampleArrayIds.filter((element: string) => element !== id);
    if (code.trim().length > 0 && newInvalidIds.indexOf(id) < 0) {
      newInvalidIds = newInvalidIds.push(id);
    } else if (code.trim().length === 0 && newInvalidIds.indexOf(id) > -1) {
      newInvalidIds = newInvalidIds.filter((element: string) => element !== id);
    }
    updatedTabs = updatedTabs.setIn([activeIndex, 'code'], code).setIn([activeIndex, 'codeError'], null);
  }

  return state.set('tabs', updatedTabs).set('invalidIds', newInvalidIds).set('exampleArrayIds', exampleArrayIds);
};

const editorReducer = handleActions<any>(
  {
    CREATE_NEW_TAB: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs: Immutable.List<EditorTab> = state.get('tabs');
      const currentIndex = tabs.size;
      const tabCountPrev: number = state.get('tabCount') || 0;
      const tabCount: number = tabCountPrev + 1;
      const currentName = `Simulation ${tabCount.toLocaleString(languageCode)}`;
      const settings = getSettings(state.get('options'), Immutable.fromJS(action.payload.data), simulationSettingsToFilter);
      // @ts-ignore
      const newElement: EditorTab = Immutable.fromJS({
        tabName: currentName,
        code: '',
        id: action.payload.idParent,
        visualization: false,
        codeError: null,
        settings,
        tabNumber: tabCount,
      });

      return state.set('activeTab', currentIndex).set('tabCount', tabCount).set('tabs', tabs.push(newElement));
    },
    CREATE_NEW_SUPER_ALPHA_TAB: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs: Immutable.List<EditorTab> = state.get('tabs');
      const currentIndex = tabs.size;
      const tabCountPrev: number = state.get('superTabCount') || 0;
      const tabCount: number = tabCountPrev + 1;
      const currentName = `Super Simulation ${tabCount.toLocaleString(languageCode)}`;
      const settings = getSettings(state.get('options'), Immutable.fromJS(action.payload.data), simulationSettingsToFilter);
      // @ts-ignore
      const newElement: EditorTab = Immutable.fromJS({
        tabName: currentName,
        combo: '',
        filter: '',
        id: action.payload.idParent,
        visualization: false,
        codeError: null,
        settings,
        superAlpha: true,
      });

      return state.set('activeTab', currentIndex).set('superTabCount', tabCount).set('tabs', tabs.push(newElement));
    },
    CREATE_NEW_MULTI_TAB: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs: Immutable.List<EditorTab> = state.get('tabs');
      const multiTabCountPrev: number = state.get('multiTabCount') || 0;
      const tabCountPrev: number = state.get('tabCount') || 0;
      const tabCount: number = tabCountPrev + 1;
      const multiTabCount = multiTabCountPrev + 1;
      const currentChildIndex = String.fromCharCode(97).toUpperCase();
      const currentChildName = `Simulation ${multiTabCount.toLocaleString(languageCode)}${currentChildIndex}`;
      const settingsChild = getSettings(state.get('options'), Immutable.fromJS(action.payload.data), multiSimulationSettingsChildToFilter);
      // @ts-ignore
      const newChildElement: EditorTab = Immutable.fromJS({
        tabName: currentChildName,
        code: '',
        id: action.payload.idChilds[0],
        codeError: null,
        settings: settingsChild,
        tabNumber: currentChildIndex,
      });
      const newChildIndex = String.fromCharCode(98).toUpperCase();
      const newChildName = `Simulation ${multiTabCount.toLocaleString(languageCode)}${newChildIndex}`;
      let newChildElement1: EditorTab = newChildElement;
      newChildElement1 = newChildElement1
        .set('tabName', newChildName)
        .set('id', action.payload.idChilds[1])
        .set('tabNumber', newChildIndex);
      const currentGeneralIndex = tabs.size;
      const currentMultiName = `Multi-Simulation ${multiTabCount.toLocaleString(languageCode)}`;
      const settings = getSettings(state.get('options'), Immutable.fromJS(action.payload.data), multiSimulationSettingsToFilter);

      // @ts-ignore
      const newMultiElement: EditorTab = Immutable.fromJS({
        tabName: currentMultiName,
        id: action.payload.idParent,
        visualization: false,
        codeError: null,
        settings,
        childTabs: [newChildElement, newChildElement1],
        activeChildTab: 0,
        childTabCount: 1,
        tabNumber: multiTabCount,
      });

      return state
        .set('activeTab', currentGeneralIndex)
        .set('tabCount', tabCount)
        .set('multiTabCount', multiTabCount)
        .set('tabs', tabs.push(newMultiElement));
    },

    CREATE_NEW_CHILD_TAB: (state: EditorStoreElement, action: EditorActionType) => createNewChildTab(state, action),

    SET_TAB_ACTIVE: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const currentSize = tabs.size;
      const activateTabAtIndex = action.payload ? action.payload.tabIndex : -1;

      return activateTabAtIndex !== undefined && activateTabAtIndex <= currentSize ? state.set('activeTab', activateTabAtIndex) : state;
    },
    SET_CHILD_TAB_ACTIVE: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const activeTab = state.get('activeTab');
      const currentChildsSize = tabs.getIn([action.payload.parentTabIndex, 'childTabs']).size;
      const activateChildTabAtIndex = action.payload ? action.payload.tabIndex : null;
      const newTabs = tabs.setIn([activeTab, 'activeChildTab'], activateChildTabAtIndex || 0);

      return activateChildTabAtIndex !== undefined && activateChildTabAtIndex !== null && activateChildTabAtIndex <= currentChildsSize
        ? state.set('tabs', newTabs)
        : state;
    },
    RESPONSE_DATA_SIMULATE_SUCCESS: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const tabIndex = tabs.findIndex((item: any) => item.get('id') === action.payload.idParent);
      let newInvalidIds = state.get('invalidIds');
      if (tabIndex >= 0) {
        if (action.payload.childId !== null && action.payload.childId !== undefined) {
          const parentTabId = tabs.getIn([tabIndex, 'id']);
          const numOfInvalidChilds = tabs.getIn([tabIndex, 'childTabs']).reduce((total: number, childTab: any) => {
            if (newInvalidIds.includes(childTab.get('id'))) {
              return total + 1;
            }

            return total;
          }, 0);
          const id = action.payload.childId;
          newInvalidIds = newInvalidIds.filter((element: string) => element !== id);
          if (numOfInvalidChilds === 1) {
            newInvalidIds = newInvalidIds.filter((element: string) => element !== parentTabId);
          }
        } else {
          const id = tabs.getIn([tabIndex, 'id']);
          newInvalidIds = newInvalidIds.filter((element: string) => element !== id);
        }

        return state.set('invalidIds', newInvalidIds).set('isSimulated', true);
      } else {
        return state.set('isSimulated', true);
      }
    },
    SET_TAB_CODE: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const activeIndex = (action.payload && action.payload.tabIndex) || 0;
      const activeChildIndex = (action.payload && action.payload.childActiveIndex) || 0;
      const codeType = action.payload.codeType ? action.payload.codeType : 'code';
      const code = action.payload ? action.payload.code : '';
      let updatedTabs = tabs;
      let newInvalidIds = state.get('invalidIds');
      let exampleArrayIds = state.get('exampleArrayIds');
      if (action.payload && action.payload.childActiveIndex !== -1) {
        const id = updatedTabs.getIn([activeIndex, 'childTabs', activeChildIndex, 'id']);
        const parentTabId = updatedTabs.getIn([activeIndex, 'id']);
        const numOfInvalidChilds = updatedTabs.getIn([activeIndex, 'childTabs']).reduce((total: number, childTab: any) => {
          if (newInvalidIds.includes(childTab.get('id'))) {
            return total + 1;
          }

          return total;
        }, 0);

        exampleArrayIds = exampleArrayIds.filter((element: string) => element !== id);
        if (code.trim().length > 0 && newInvalidIds.indexOf(id) < 0) {
          newInvalidIds = newInvalidIds.push(id);
          if (numOfInvalidChilds === 0) {
            newInvalidIds = newInvalidIds.push(parentTabId);
          }
        } else if (code.trim().length === 0 && newInvalidIds.indexOf(id) > -1) {
          newInvalidIds = newInvalidIds.filter((element: string) => element !== id);
          if (numOfInvalidChilds === 1) {
            newInvalidIds = newInvalidIds.filter((element: string) => element !== parentTabId);
          }
        }
        updatedTabs = updatedTabs
          .setIn([activeIndex, 'childTabs', activeChildIndex, codeType], code)
          .setIn([activeIndex, 'childTabs', activeChildIndex, 'codeError'], null);
      } else {
        const id = updatedTabs.getIn([activeIndex, 'id']);
        exampleArrayIds = exampleArrayIds.filter((element: string) => element !== id);
        if (code.trim().length > 0 && newInvalidIds.indexOf(id) < 0) {
          newInvalidIds = newInvalidIds.push(id);
        } else if (code.trim().length === 0 && newInvalidIds.indexOf(id) > -1) {
          newInvalidIds = newInvalidIds.filter((element: string) => element !== id);
        }
        updatedTabs = updatedTabs.setIn([activeIndex, codeType], code).setIn([activeIndex, 'codeError'], null);
      }

      return state.set('tabs', updatedTabs).set('invalidIds', newInvalidIds).set('exampleArrayIds', exampleArrayIds);
    },

    CLONE_ALPHA_FROM_SIMULATE: (state: EditorStoreElement, action: EditorActionType) => {
      const code = action.payload && action.payload.data.regular ? action.payload.data.regular : '';
      const settings = getSettings(state.get('options'), Immutable.fromJS(action.payload.data.settings), simulationSettingsToFilter);
      const stateNewCode = updateCodeTabActive(state, action, code);

      return updateSettingsTabActive(stateNewCode, action, settings);
    },

    SET_TAB_LEARN_PATHNAME: (state: EditorStoreElement, action: EditorActionType) => {
      const pathname = action.payload ? action.payload.value : '';

      return state.set('learnPathname', pathname);
    },
    SET_TAB_DATA_EXPLORER_PATHNAME: (state: EditorStoreElement, action: EditorActionType) => {
      const pathname = action.payload ? action.payload.value : '';

      return state.set('dataExplorerPathname', pathname);
    },
    SET_TAB_TUTORIAL_PATHNAME: (state: EditorStoreElement, action: EditorActionType) => {
      const pathname = action.payload ? action.payload.value : '';

      return state.set('tutorialPathname', pathname);
    },
    REQUEST_SELECTION_CODE: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const tabIndex = tabs.findIndex((item: any) => item.get('id') === action.payload.idParent);
      if (tabIndex >= 0) {
        return state.setIn(['tabs', tabIndex, 'codeError'], null);
      } else {
        return state;
      }
    },
    REQUEST_SIMULATION_POST: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const tabIndex = tabs.findIndex((item: any) => item.get('id') === action.payload.idParent);
      if (tabIndex >= 0) {
        return state.setIn(['tabs', tabIndex, 'codeError'], null);
      } else {
        return state;
      }
    },

    SET_ALL_EXAMPLES: (state: EditorStoreElement, action: EditorActionType) => {
      return state.set('examples', Immutable.fromJS(action.payload.examples));
    },

    SET_TAB_EXAMPLE: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const activeIndex = (action.payload && action.payload.tabIndex) || 0;
      const code = action.payload?.example?.regular ?? '';
      let updatedTabs = tabs;
      let newInvalidIds = state.get('invalidIds');
      let exampleArrayIds = state.get('exampleArrayIds');
      if (action.payload && action.payload.childActiveIndex !== -1) {
        return state;
      } else {
        const id = updatedTabs.getIn([activeIndex, 'id']);
        const existsCode = code !== undefined && code !== null;
        if (existsCode && code.trim().length > 0 && exampleArrayIds.indexOf(id) < 0) {
          exampleArrayIds = exampleArrayIds.push(id);
        } else if (existsCode && code.trim().length === 0 && exampleArrayIds.indexOf(id) > -1) {
          exampleArrayIds = exampleArrayIds.filter((element: string) => element !== id);
        }

        if (existsCode && code.trim().length > 0 && newInvalidIds.indexOf(id) < 0) {
          newInvalidIds = newInvalidIds.push(id);
        } else if (existsCode && code.trim().length === 0 && newInvalidIds.indexOf(id) > -1) {
          newInvalidIds = newInvalidIds.filter((element: string) => element !== id);
        }
        updatedTabs = updatedTabs.setIn(
          [activeIndex, 'settings'],
          Immutable.fromJS(
            getSettings(state.get('options'), Immutable.fromJS(action.payload?.example?.settings), simulationSettingsToFilter)
          )
        );
        updatedTabs = updatedTabs.setIn([activeIndex, 'code'], code).setIn([activeIndex, 'codeError'], null);
      }

      return state.set('tabs', updatedTabs).set('invalidIds', newInvalidIds).set('exampleArrayIds', exampleArrayIds);
    },

    SET_TAB_CODE_ERROR: (state: EditorStoreElement, action: EditorActionType) => {
      const codeErrorObject = action.payload.codeError;
      const codeError = {
        ...action.payload.codeError,
        start: codeErrorObject && checkIfValueExists(codeErrorObject.start) ? parseInt(codeErrorObject.start, 10) : null,
        line: codeErrorObject && checkIfValueExists(codeErrorObject.line) ? parseInt(codeErrorObject.line, 10) : null,
        end: codeErrorObject && checkIfValueExists(codeErrorObject.end) ? parseInt(codeErrorObject.end, 10) : null,
      };
      if (action.payload && action.payload.childActiveIndex !== null) {
        return state.update('tabs', (tabs: any) =>
          tabs.map((elem: any) => {
            if (elem.get('id') === action.payload.tabIndex && elem.get('childTabs')) {
              return elem.update('childTabs', (childTabs: any) =>
                childTabs.map((elemChild: any) => {
                  if (elem.getIn(['childTabs', action.payload.childActiveIndex, 'id']) === elemChild.get('id')) {
                    return elemChild.set('codeError', Immutable.fromJS(codeError));
                  } else {
                    return elemChild;
                  }
                })
              );
            } else {
              return elem;
            }
          })
        );
      } else {
        return state.update('tabs', (tabs: any) =>
          tabs.map((elem: any) => {
            if (elem.get('id') === action.payload.tabIndex) {
              return elem.set('codeError', Immutable.fromJS(codeError));
            } else {
              return elem;
            }
          })
        );
      }
    },

    UPDATE_SETTINGS_ACTIVE_TAB: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const activeIdx = (action.payload && action.payload.tabIndex) || 0;
      let allTabs = tabs;
      if (action.payload && action.payload.childActiveIndex !== -1) {
        const parentSettings = getSettingsGivenOtherSettings(Immutable.fromJS(action.payload.settings), multiSimulationSettingsToFilter);
        const childSettings = getSettingsGivenOtherSettings(
          Immutable.fromJS(action.payload.settings),
          multiSimulationSettingsChildToFilter
        );
        allTabs = allTabs
          .setIn([activeIdx, 'settings'], parentSettings)
          .setIn([activeIdx, 'childTabs', action.payload.childActiveIndex || 0, 'settings'], childSettings);
        const childTabs = allTabs
          .getIn([activeIdx, 'childTabs'])
          .map((tab: any) => tab.set('settings', checkIfSettingsAreInvalid(tab.get('settings'), parentSettings, state.get('options'))));
        allTabs = allTabs.setIn([activeIdx, 'childTabs'], childTabs);
      } else {
        allTabs = allTabs.setIn([activeIdx, 'settings'], Immutable.fromJS(action.payload.settings));
      }

      return state.set('tabs', allTabs);
    },

    AUTHENTICATION_LOGIN: (state: EditorStoreElement, action: EditorActionType) => state.set('userId', action.payload.user.id),

    CLOSE_TAB: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const activeTab = state.get('activeTab');
      const removeTabAtIndex = action.payload ? action.payload.tabIndex : null;
      if (removeTabAtIndex === null) {
        return state;
      }

      const tabToDelete = tabs.get(removeTabAtIndex);

      if (!tabToDelete) {
        return state;
      }
      let newInvalidIds = state.get('invalidIds');
      let exampleArrayIds = state.get('exampleArrayIds');

      if (tabToDelete.get('childTabs')) {
        tabToDelete.get('childTabs').map((tabChild: any) => {
          newInvalidIds = newInvalidIds.filter((value: string) => value !== tabChild.get('id'));
          exampleArrayIds = exampleArrayIds.filter((value: string) => value !== tabChild.get('id'));
        });
        newInvalidIds = newInvalidIds.filter((value: string) => value !== tabToDelete.get('id'));
        exampleArrayIds = exampleArrayIds.filter((value: string) => value !== tabToDelete.get('id'));
      } else {
        newInvalidIds = newInvalidIds.filter((value: string) => value !== tabToDelete.get('id'));
        exampleArrayIds = exampleArrayIds.filter((value: string) => value !== tabToDelete.get('id'));
      }

      const clonedTabsList = tabs.remove(removeTabAtIndex);

      let newActiveTab = Math.max(0, activeTab < (removeTabAtIndex || 0) ? activeTab : activeTab - 1);
      let tabCount = state.get('tabCount');
      let multiTabCount = state.get('multiTabCount');
      let superTabCount = state.get('superTabCount');
      if (tabs.size === 1) {
        newActiveTab = -1;
        tabCount = 0;
        multiTabCount = 0;
        superTabCount = 0;
      }

      return state
        .set('activeTab', newActiveTab)
        .set('tabs', clonedTabsList)
        .set('superTabCount', superTabCount)
        .set('multiTabCount', multiTabCount)
        .set('tabCount', tabCount)
        .set('invalidIds', newInvalidIds)
        .set('exampleArrayIds', exampleArrayIds);
    },
    CLOSE_CHILD_TAB: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const activeTab = action.payload.tabIndex || 0;
      const activeChildTab = tabs.getIn([activeTab, 'activeChildTab']);
      const removeTabAtIndex = action.payload ? action.payload.indexChild : null;
      if (removeTabAtIndex === null) {
        return state;
      }

      let clonedChildTabsList = tabs.getIn([activeTab, 'childTabs']);

      clonedChildTabsList = clonedChildTabsList.remove(removeTabAtIndex);

      const newActiveTab = Math.max(0, activeChildTab < (removeTabAtIndex || 0) ? activeChildTab : activeChildTab - 1);

      const tabToDelete = tabs.getIn([activeTab, 'childTabs', removeTabAtIndex]);
      const newInvalidIds = state.get('invalidIds').filter((value: string) => value !== tabToDelete.get('id'));
      const exampleArrayIds = state.get('exampleArrayIds').filter((value: string) => value !== tabToDelete.get('id'));
      const currentTabs = tabs;
      const currentChildElems = tabs.get(activeTab).set('activeChildTab', newActiveTab).set('childTabs', clonedChildTabsList);

      return state
        .set('tabs', currentTabs.set(activeTab, currentChildElems))
        .set('invalidIds', newInvalidIds)
        .set('exampleArrayIds', exampleArrayIds);
    },
    CHANGE_TAB_POSITION: (state: EditorStoreElement, action: EditorActionType) => {
      const tabToChange = state.getIn(['tabs', action.payload.source]);
      const tabs = state.get('tabs');
      const newTabsOrder = tabs.delete(action.payload.source).insert(action.payload.destination, tabToChange);

      return state.set('tabs', newTabsOrder).set('activeTab', action.payload.destination);
    },
    CHANGE_TAB_POSITION_CHILD: (state: EditorStoreElement, action: EditorActionType) => {
      const tabToChange = state.getIn(['tabs', action.payload.currentActiveTab, 'childTabs', action.payload.source]);
      const tabs = state.getIn(['tabs', action.payload.currentActiveTab, 'childTabs']);
      // @ts-ignore
      const newTabsOrder = tabs.delete(action.payload.source).insert(action.payload.destination, tabToChange);

      return state
        .setIn(['tabs', action.payload.currentActiveTab, 'childTabs'], newTabsOrder)
        .setIn(['tabs', action.payload.currentActiveTab, 'activeChildTab'], action.payload.destination);
    },
    CHANGE_TO_SIMULATION: (state: EditorStoreElement, action: EditorActionType) => {
      const activeTab = state.getIn(['tabs', action.payload.tabIndex]);
      const childTabIndexToKeep = Math.abs((action.payload.indexChild || 0) - 1);
      const tabCountPrev: number = state.get('tabCount') || 0;
      const tabCount: number = tabCountPrev + 1;
      const currentName = `Simulation ${tabCount.toLocaleString(languageCode)}`;
      // @ts-ignore
      const childTab = activeTab.getIn(['childTabs', childTabIndexToKeep]);
      let newInvalidIds = state.get('invalidIds');
      let exampleArrayIds = state.get('exampleArrayIds');
      const childIds = [
        // @ts-ignore
        activeTab.getIn(['childTabs', action.payload.indexChild, 'id']),
        // @ts-ignore
        activeTab.getIn(['childTabs', childTabIndexToKeep, 'id']),
      ];
      newInvalidIds = newInvalidIds.filter((value: string) => !childIds.includes(value));
      exampleArrayIds = exampleArrayIds.filter(
        (value: string) =>
          // @ts-ignore
          value !== activeTab.getIn(['childTabs', action.payload.indexChild, 'id']) ||
          // @ts-ignore
          value !== activeTab.getIn(['childTabs', childTabIndexToKeep, 'id'])
      );
      // @ts-ignore
      const newElement: EditorTab = Immutable.fromJS({
        tabName: currentName,
        // @ts-ignore
        visualization: activeTab.get('visualization'),
        // @ts-ignore
        id: activeTab.get('id'),
        code: childTab.get('code'),
        // @ts-ignore
        settings: childTab.get('settings').concat(activeTab.get('settings')),
        tabNumber: tabCount,
      });

      return state
        .setIn(['tabs', action.payload.tabIndex], newElement)
        .set('tabCount', tabCount)
        .set('invalidIds', newInvalidIds)
        .set('exampleArrayIds', exampleArrayIds);
    },
    CLONE_ALPHA_FROM_CONTENT: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const alphaDetail = Immutable.fromJS(action.payload.data);
      const type = alphaDetail.get('type');
      const tabCountPrev: number = state.get(type === 'SUPER' ? 'superTabCount' : 'tabCount') || 0;
      const tabCount: number = tabCountPrev + 1;
      const currentIndex = state.get('tabs').size;
      const name = type === 'SUPER' ? 'Super Simulation' : 'Simulation';
      const currentName = `${name} ${tabCount.toLocaleString(languageCode)}`;
      let newInvalidIds = state.get('invalidIds');
      const regular = alphaDetail.getIn(['regular', 'code']) || alphaDetail.get('regular') || undefined;
      const selection = alphaDetail.getIn(['selection', 'code']) ? alphaDetail.getIn(['selection', 'code']) : undefined;
      const combo = alphaDetail.getIn(['combo', 'code']) ? alphaDetail.getIn(['combo', 'code']) : undefined;
      // @ts-ignore
      if ((regular && regular.trim().length > 0) || (selection && selection.trim().length > 0) || (combo && combo.trim().length > 0)) {
        newInvalidIds = newInvalidIds.push(action.payload.idParent);
      }
      // @ts-ignore
      const newElement: EditorTab = Immutable.fromJS({
        tabName: currentName,
        code: regular,
        selection,
        combo,
        superAlpha: type === 'SUPER',
        id: action.payload.idParent,
        visualization: alphaDetail.getIn(['settings', 'visualization']),
        settings: getSettings(state.get('options'), alphaDetail.get('settings'), simulationSettingsToFilter),
        tabNumber: tabCount,
      });

      return state
        .set(type === 'SUPER' ? 'superTabCount' : 'tabCount', tabCount)
        .set('activeTab', currentIndex)
        .set('tabs', tabs.push(newElement))
        .set('invalidIds', newInvalidIds);
    },
    CLONE_ALPHA: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const activeTab = state.getIn(['tabs', action.payload.tabIndex]);
      const isMulti = action.payload.isMulti;
      // @ts-ignore
      const isSuperAlpha = activeTab.get('combo') !== undefined && activeTab.get('combo') !== null;
      const multiTabCountPrev: number = state.get('multiTabCount') || 0;
      const tabCountPrev: number = state.get(isSuperAlpha ? 'superTabCount' : 'tabCount') || 0;
      const tabCount: number = isMulti ? multiTabCountPrev + 1 : tabCountPrev + 1;
      const name = isSuperAlpha ? 'Super Simulation' : 'Simulation';
      const currentName = `${name} ${tabCount.toLocaleString(languageCode)}`;
      let code;
      let settings;
      let newInvalidIds = state.get('invalidIds');
      if (isMulti) {
        // @ts-ignore
        const newActiveTab = activeTab
          .set('id', action.payload.idParent)
          .update('childTabs', (childTab: any) =>
            childTab.map((el: any, index: number) => {
              if (el.get('code').trim().length > 0) {
                newInvalidIds = newInvalidIds.push(action.payload.idChilds[index]);
                if (newInvalidIds.indexOf(action.payload.idParent) === -1) {
                  newInvalidIds = newInvalidIds.push(action.payload.idParent);
                }
              }

              return el.set('id', action.payload.idChilds[index]);
            })
          )
          .set('tabName', `Multi-Simulation ${tabCount.toLocaleString(languageCode)}`);

        return state
          .set('multiTabCount', tabCount)
          .set('tabs', tabs.push(newActiveTab))
          .set('activeTab', tabs.size)
          .set('invalidIds', newInvalidIds);
      } else if (action.payload.indexChild !== null) {
        // @ts-ignore
        code = activeTab.getIn(['childTabs', action.payload.indexChild, 'code']);
        // @ts-ignore
        settings = activeTab.getIn(['childTabs', action.payload.indexChild, 'settings']).concat(activeTab.get('settings'));
        if (code.trim().length > 0) {
          return createNewChildTab(state, action, settings, code, true);
        } else {
          return createNewChildTab(state, action, settings, code);
        }
      } else {
        // @ts-ignore
        code = activeTab.get('code');
        // @ts-ignore
        settings = activeTab.get('settings');
        if (code !== undefined && code !== null && code.trim().length > 0) {
          // @ts-ignore
          newInvalidIds = newInvalidIds.push(activeTab.get('id'));
        }
      }

      // @ts-ignore
      const newElement: EditorTab = Immutable.fromJS({
        tabName: currentName,
        id: action.payload.idParent,
        code,
        // @ts-ignore
        visualization: activeTab.get('visualization'),
        settings,
        // @ts-ignore
        combo: activeTab.get('combo'),
        // @ts-ignore
        selection: activeTab.get('selection'),
        // @ts-ignore
        superAlpha: activeTab.get('superAlpha'),
        tabNumber: tabCount,
      });

      return state
        .set(isSuperAlpha ? 'superTabCount' : 'tabCount', tabCount)
        .set('tabs', tabs.push(newElement))
        .set('invalidIds', newInvalidIds)
        .set('activeTab', tabs.size);
    },
    CHANGE_TO_MULTI_TAB: (state: EditorStoreElement, action: EditorActionType) => {
      const tabs = state.get('tabs');
      const activeTabIndex = state.get('activeTab');
      const activeTab = tabs.get(activeTabIndex);
      const multiTabCountPrev: number = state.get('multiTabCount') || 0;
      const multiTabCount = multiTabCountPrev + 1;
      const currentChildIndex = String.fromCharCode(97).toUpperCase();
      const currentChildName = `Simulation ${multiTabCount.toLocaleString(languageCode)}${currentChildIndex}`;
      // @ts-ignore
      const newChildElement: EditorTab = Immutable.fromJS({
        tabName: currentChildName,
        code: activeTab.get('code'),
        id: action.payload.idChilds[0],
        settings: multiSimulationSettingsChildToFilter.reduce((map: any, key: string) => map.delete(key), activeTab.get('settings')),
        tabNumber: currentChildIndex,
      });
      const newChildIndex = String.fromCharCode(98).toUpperCase();
      const newChildName = `Simulation ${multiTabCount.toLocaleString(languageCode)}${newChildIndex}`;
      let newChildElement1: EditorTab = newChildElement;
      newChildElement1 = newChildElement1
        .set('tabName', newChildName)
        .set('id', action.payload.idChilds[1])
        .set('tabNumber', newChildIndex);
      const currentMultiName = `Multi-Simulation ${multiTabCount.toLocaleString(languageCode)}`;
      // @ts-ignore
      const newMultiElement: EditorTab = Immutable.fromJS({
        tabName: currentMultiName,
        id: action.payload.idParent,
        visualization: activeTab.get('visualization'),
        settings: multiSimulationSettingsToFilter.reduce((map: any, key: string) => map.delete(key), activeTab.get('settings')),
        childTabs: [newChildElement, newChildElement1],
        activeChildTab: 1,
        childTabCount: 1,
        tabNumber: multiTabCount,
      });

      let newInvalidIds = state.get('invalidIds');
      let exampleArrayIds = state.get('exampleArrayIds');
      const id = activeTab.get('id');
      exampleArrayIds = exampleArrayIds.filter((element: string) => element !== id);
      if (activeTab.get('code').trim().length > 0 && newInvalidIds.indexOf(id) > -1) {
        newInvalidIds = newInvalidIds.filter((element: string) => element !== id);
        newInvalidIds = newInvalidIds.push(action.payload.idChilds[0]);
        newInvalidIds = newInvalidIds.push(action.payload.idChilds[1]);
        newInvalidIds = newInvalidIds.push(action.payload.idParent);
      }

      return state
        .set('multiTabCount', multiTabCount)
        .set('invalidIds', newInvalidIds)
        .set('exampleArrayIds', exampleArrayIds)
        .setIn(['tabs', activeTabIndex], newMultiElement);
    },
    RESPONSE_OPTIONS: (state: EditorStoreElement, action: EditorActionType) =>
      state.set('options', Immutable.fromJS(action.payload)).set('fetchingSettings', false).set('loadedOptions', true),

    REQUEST_OPTIONS: (state: EditorStoreElement) =>
      state.merge(
        Immutable.fromJS({
          fetchingSettings: true,
        })
      ),
    RESPONSE_OPTIONS_FAILURE: (state: EditorStoreElement) =>
      state.merge(
        Immutable.fromJS({
          fetchingSettings: false,
          loadedOptions: false,
        })
      ),
    OPTIONS_LOGOUT: (state: EditorStoreElement) =>
      state.merge(
        Immutable.fromJS({
          fetchingSettings: false,
          loadedOptions: false,
        })
      ),
    TOGGLE_VISUALIZATION: (state: EditorStoreElement, action: EditorActionType) => {
      const index = action.payload;
      const currentVisualization = state.getIn(['tabs', index, 'visualization']);

      return state.setIn(['tabs', index, 'visualization'], !currentVisualization);
    },
    RENAME_TAB: (state: EditorStoreElement, action: EditorActionType) => {
      const isChild = action.payload.indexChild;
      const currentIndex = action.payload.tabIndex;
      const newName = action.payload.newName;
      if (isChild !== null) {
        return state.setIn(['tabs', currentIndex, 'childTabs', isChild, 'tabName'], newName);
      } else {
        return state.setIn(['tabs', currentIndex, 'tabName'], newName);
      }
    },
    WALKTHROUGH_ADD_CODE: (state: EditorStoreElement) => {
      const tabs: Immutable.List<EditorTab> = state.get('tabs');
      const currentIndex = tabs.size - 1;

      return state
        .setIn(['tabs', currentIndex, 'code'], WALKTHROUGH_EXAMPLE_CODE)
        .setIn(['tabs', currentIndex, 'settings', 'testPeriod', 'value'], 'P1Y0M0D');
    },
    WALKTHROUGH_REMOVE_CODE: (state: EditorStoreElement) => {
      const tabs: Immutable.List<EditorTab> = state.get('tabs');
      const currentIndex = tabs.size - 1;

      return state.setIn(['tabs', currentIndex, 'code'], '');
    },
    TOGGLE_MONACO_INIT: (state: EditorStoreElement) => state.set('isMonacoInitialized', true),
    CHANGING_TAB_POSITION: (state: EditorStoreElement) => state.set('isChangingTabPosition', true),
    STOP_CHANGING_TAB_POSITION: (state: EditorStoreElement) => state.set('isChangingTabPosition', false),
    AUTHENTICATION_LOGOUT: () => initialState,
    CHANGE_CODE_EDITOR_WIDTH: (state: EditorStoreElement, action) => state.set('codeEditorWidth', action.payload),
  },
  initialState
);

export default editorReducer;
